// PUBLISH A NEW EXCHANGE RATE -- Settings > Exchange Rates, ADMIN ONLY.
//
// ⚠️ A PRICING INPUT, AND THE REASON THIS FORM IS CAREFUL. An FX rate is read
// by anixify.quote.line._resolve_conversion_rate into conversion_rate, which
// the engine multiplies straight into value_in_inr. Fence (b) of the standing
// direction forbids a Settings screen becoming "a route to a pricing input by
// the back door" -- so this is built with the discount-rule discipline: an
// Admin only, a new row every time, the author recorded from the session, and
// no row ever edited except the one window close the backend performs.
//
// ⚠️ THE FORM IS NOT THE GUARD. It is rendered only for an Admin, but that is
// cosmetic: /anixify/api/save_fx_rate refuses Sales and Viewer with a 403
// whatever this screen does. Every check below is also made by the backend,
// which is the authority; the ones here only save a round trip.
//
// ⚠️ THE TYPO GUARD WARNS AND NEVER BLOCKS. A rate more than 10% from the one
// in force today is worth a second look, and currencies do move that far, so
// the person confirms and it saves. The comparison reads the published
// schedule through rateInForce, which mirrors the backend's "exactly one row"
// rule and answers null rather than choosing when it cannot.

import { useEffect, useState } from "react";
import { listCurrencies, saveFxRate, type CurrencyOption } from "@/services/api";
import {
  rateInForce, percentDifferent, TYPO_GUARD_PCT, todayIso,
} from "@/services/fxInForce";
import type { FxRate } from "@/types";
import {
  colors, semanticColors, radius, spacing, heights, typography,
} from "@/styles/tokens";

export const TYPO_GUARD_TEXT = (pct: number) =>
  `This is ${pct.toFixed(1)}% different from the current rate — confirm?`;

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box",
};

const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};

const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  // onAccent, never white: tokens.ts says so on the accent itself.
  color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium,
};

export function PublishFxRate({
  rates, onPublished,
}: {
  /** The whole published schedule, as the page loaded it. */
  rates: FxRate[];
  /** Called after a rate is really published, so the table re-reads. */
  onPublished: () => void;
}) {
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [currency, setCurrency] = useState("");
  const [rate, setRate] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmPct, setConfirmPct] = useState<number | null>(null);
  const [published, setPublished] = useState("");

  useEffect(() => {
    let live = true;
    // ⚠️ ONLY entries with a code, each once. A currency with no ISO code
    // cannot be published -- the backend looks rates up by code -- and a list
    // carrying duplicates or blanks gave React colliding keys (caught by
    // allRoutes.console.test's clean-console rule, not by eye).
    void listCurrencies().then((list) => {
      if (!live) return;
      const seen = new Set<string>();
      setCurrencies(list.filter((c) => {
        const code = (c?.code ?? "").trim();
        if (!code || seen.has(code)) return false;
        seen.add(code);
        return true;
      }));
    });
    return () => { live = false; };
  }, []);

  const today = todayIso();
  const current = currency ? rateInForce(rates, currency, today) : null;

  const reset = () => {
    setConfirmPct(null);
    setError("");
  };

  const publish = async (confirmed: boolean) => {
    setError("");
    setPublished("");
    const value = Number(rate);
    if (!currency) { setError("Choose the currency this rate is for."); return; }
    if (!(value > 0) || !Number.isFinite(value)) {
      setError("A rate must be a number greater than 0.");
      return;
    }
    if (!effectiveFrom) { setError("Give the date this rate applies from."); return; }
    if (effectiveFrom < today) {
      setError("A rate cannot be back-dated — it would silently re-price drafts as of an earlier date.");
      return;
    }

    // ⚠️ THE TYPO GUARD. Only asked once per attempt; a confirmed publish
    // goes straight through.
    const pct = percentDifferent(value, current);
    if (!confirmed && pct !== null && pct > TYPO_GUARD_PCT) {
      setConfirmPct(pct);
      return;
    }

    setBusy(true);
    const result = await saveFxRate(currency, value, effectiveFrom);
    setBusy(false);
    setConfirmPct(null);
    if (!result.ok) {
      setError(result.error ?? "The rate was not published.");
      return;
    }
    setPublished(
      `Published ${currency} ${value} from ${effectiveFrom}. `
      + (current !== null ? `The previous rate (${current}) now ends the day before.` : ""),
    );
    setRate("");
    onPublished();
  };

  return (
    <div
      data-testid="fx-publish"
      style={{
        marginBottom: spacing[12], padding: spacing[12],
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.panelBg,
        display: "flex", flexDirection: "column", gap: spacing[8],
      }}
    >
      <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
        <span style={{ fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>
          Publish a rate.
        </span>{" "}
        A new rate is a new row: the one in force now ends the day before it
        starts, and nothing else about it changes. Finalised quotes never move;
        drafts keep the rate they were priced at until someone chooses today&rsquo;s.
      </div>

      <div style={{ display: "flex", gap: spacing[8], alignItems: "center", flexWrap: "wrap" }}>
        <select
          data-testid="fx-publish-currency"
          value={currency}
          onChange={(e) => { setCurrency(e.target.value); reset(); }}
          style={INPUT}
        >
          <option value="">Currency…</option>
          {currencies.map((c) => (
            <option key={c.code} value={c.code}>{c.code}</option>
          ))}
        </select>
        <input
          data-testid="fx-publish-rate"
          type="number" step="any" min="0" placeholder="INR per unit"
          value={rate}
          onChange={(e) => { setRate(e.target.value); reset(); }}
          style={{ ...INPUT, width: 140 }}
        />
        <input
          data-testid="fx-publish-from"
          type="date" min={today}
          value={effectiveFrom}
          onChange={(e) => { setEffectiveFrom(e.target.value); reset(); }}
          style={INPUT}
        />
        <button
          data-testid="fx-publish-submit"
          onClick={() => void publish(false)}
          disabled={busy}
          style={{ ...PRIMARY, cursor: busy ? "not-allowed" : "pointer" }}
        >
          {busy ? "Publishing…" : "Publish rate"}
        </button>
        {currency && (
          <span data-testid="fx-publish-current"
                style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
            {current !== null ? `In force today: ${current}` : "No single rate in force today"}
          </span>
        )}
      </div>

      {confirmPct !== null && (
        <div
          data-testid="fx-publish-typo-guard"
          style={{
            padding: spacing[8], borderRadius: radius.base,
            backgroundColor: colors.amber50, border: `1px solid ${colors.amber200}`,
            color: colors.amber800, fontSize: typography.fontSize.sm,
            display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap",
          }}
        >
          <span>{TYPO_GUARD_TEXT(confirmPct)}</span>
          <button data-testid="fx-publish-confirm" onClick={() => void publish(true)}
                  disabled={busy} style={BUTTON}>
            Publish anyway
          </button>
          <button data-testid="fx-publish-cancel" onClick={reset} disabled={busy} style={BUTTON}>
            Cancel
          </button>
        </div>
      )}

      {error !== "" && (
        <div
          data-testid="fx-publish-error"
          style={{
            padding: spacing[8], borderRadius: radius.base,
            backgroundColor: semanticColors.destructiveBg,
            border: `1px solid ${semanticColors.destructiveBorder}`,
            color: semanticColors.destructive, fontSize: typography.fontSize.sm,
          }}
        >
          {error}
        </div>
      )}

      {published !== "" && (
        <div data-testid="fx-publish-done"
             style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          {published}
        </div>
      )}
    </div>
  );
}
